import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

// Modular D3 imports for performance
import { select, pointer, Selection } from 'd3-selection';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear, scaleLog } from 'd3-scale';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { line, curveMonotoneX } from 'd3-shape';
import 'd3-transition'; // Required for .transition() method

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ThemeService, Theme } from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type colorLegend = `#${string}`;

// --- Added Interfaces for better Type Safety ---
export interface BarChartData {
  start: string;
  end: string;
  label?: string;
  key?: string;
  month_short?: string;
  type?: 'bar' | 'line';
  [key: string]: any; // For dynamic keys like 'total', 'dental', etc.
}

export interface LegendData {
  label: string;
  color: colorLegend;
  type?: 'square' | 'circle' | 'line' | 'dashed-line' | 'square-outline';
}

export interface ColorMap {
  data1: colorLegend | '';
  data2: colorLegend | '';
  data3: colorLegend | '';
  data4: colorLegend | '';
}

export interface TooltipConfig {
  label: string;
  key: string;
  type?: 'percent' | string;
}
// ---

@Component({
  selector: 'app-multiple-bar',
  templateUrl: './multiple-bar.component.html',
  styleUrls: ['./multiple-bar.component.scss'],
  providers: [DatePipe],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    'class': 'w-full h-full block'
  }
})
export class MultipleBarComponent
  implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  @Input() data3: BarChartData[] = [];
  @Input() data1: BarChartData[] = [];
  @Input() data2: BarChartData[] = [];
  @Input() data4: BarChartData[] = [];
  @Input() legendData: LegendData[] = [];
  @Input() showLegends: boolean = true;
  @Input() showYAxis: boolean = true;
  @Input() yAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllYticks: boolean = false;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllXticks: boolean = false;
  @Input() legendPosition: 'left' | 'center' | 'right' = 'left';
  @Input() legendGap: number = 80;
  @Input() showDynamicxAxisLabel: boolean = false;
  @Input() barClusterPixel: number = 40;
  @Input() barClusterHeight: number = 40;
  @Input() darkBarOutlineFill: string = '';
  @Input() lightBarOutlineFill: string = '';
  @Input() unit: string = '';
  @Input() showTooltipLabel: boolean = true;
  @Input() scaleType: 'linear' | 'log' = 'linear';
  @Input() interactiveLegends: boolean = false;
  @Input() margin: { top: number; right: number; bottom: number; left: number } = { top: 30, right: 0, bottom: 30, left: 40 };
  @Input() showCard: boolean = true;
  @Input() xAxisFontSize: string = '14px';

  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private combinedData: (BarChartData & { color: colorLegend })[][] = [];
  @Input() gap: number = 2; // Default back to 2
  @Input() groupPadding: number = 0.2;
  @Input() innerPadding: number = 4; // Default padding to restore +4 behavior
  @Input() fitBarsToWidth: boolean = false;
  @Input() barWidthDivisor: number = 0;
  @Input() maxBarWidth: number = 0;
  @Input() showBarValues: boolean = true;
  @Input() legendIconType: 'square' | 'circle' = 'square';
  @Input() barStyle: 'solid' | 'accent-top' = 'solid';
  @Input() barRadius: number = 6;
  @Input() showGrid: boolean = true;
  @Input() hoverOpacity: number = 0.05;
  @Input() showBgRect: boolean = true;
  @Input() reducedHighlight: boolean = false;
  @Input() xScalePadding: number = 0;
  @Input() barHeightRatio: number = 0.55;
  @Input() showStripes: boolean = false;
  @Input() yMax: number = 0;
  @Input() autoScrollOn5Years: boolean = false;
  @Input() autoScrollOn3Years: boolean = false;
  chartId = 'chart-' + Math.random().toString(36).substr(2, 9);

  fullBarWidth: number = 0;

  @Input() colors: ColorMap = {
    data1: '',
    data2: '',
    data3: '',
    data4: '',
  };
  @Input() valueKey = 'total';
  @Input() otherKey: string = '';
  @Input() valueType: '$' | '%' | 'number' = '%';
  @Input() addGradient: boolean = true;
  @Input() barTextposition: string = 'top';
  @Input() barTextColor: colorLegend = '' as colorLegend;
  @Input() tooltipData: TooltipConfig[] = [
    { label: 'Dental', key: 'dental' },
    { label: 'HealthCare', key: 'healthcare' },
    { label: 'Non HealthCare', key: 'non_healthcare' },
  ];
  @Input() height: number | 'auto' = 350;
  @Input() useStandardizedScaling: boolean = false;

  private actualWidth: number = 0;
  private actualHeight: number = 0;

  @Input() labelKey: any;
  @Input() flatBars: boolean = false;

  private themeService = inject(ThemeService);
  constructor(private datePipe: DatePipe) { }

  ngAfterViewInit() {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.renderChart(); // Redraw chart with new theme colors
    });

    this.observeResize();
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data1'] || changes['data2'] || changes['data3'] || changes['data4'] || changes['scaleType'] || changes['xScalePadding']) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    // Cleanup body tooltips for this instance
    this.tooltipDiv?.remove();
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // Bar colors - using different shades for multiple bars
      primaryBar: this.colors.data1 || (isDark ? '#f472b6' : '#b927ff'), // pink-400 / pink-500
      secondaryBar: this.colors.data2 || (isDark ? '#a78bfa' : '#8b5cf6'), // purple-400 / purple-600
      tertiaryBar: this.colors.data3 || (isDark ? '#818cf8' : '#6366f1'), // indigo-400 / indigo-500
      forthBar: this.colors.data4 || (isDark ? '#81f891ff' : '#ec63f1ff'), // indigo-400 / indigo-500

      // Text colors
      barTextColor: this.barTextColor || (isDark ? '#f4f4f5' : '#000000'), // slate-100 / black
      axisText: isDark ? '#d4d4d8' : '#64748b', // slate-300 / slate-500
      axisLine: isDark ? '#85858e' : '#94a3b8',
      gridLine: isDark ? '#2e2e33' : '#E5E7EB',

      // Background gradients
      gradientNormalStart: isDark ? '#1e1e21' : '#E7EFF8', // slate-800 / light blue
      gradientNormalEnd: isDark ? '#151517' : '#FFFFFF', // slate-900 / white
      gradientReversedStart: isDark ? '#151517' : '#FFFFFF', // slate-900 / white
      gradientReversedEnd: isDark ? '#1e1e21' : '#E7EFF8', // slate-800 / light blue

      // Bar background/outline
      barOutlineFill: isDark && this.darkBarOutlineFill ? this.darkBarOutlineFill : this.lightBarOutlineFill ? this.lightBarOutlineFill : isDark ? '#1e1e21' : '#fff', // slate-800 / white
      // barOutlineFill: isDark ? '#1e1e21' : '#fff', // slate-800 / white
      barOutlineStroke: isDark ? '#47474e' : '#CBD1D9', // slate-600 / gray-300

      // Tooltip colors
      tooltipBg: isDark ? '#232327' : '#ffffff', // slate-800 / white
      tooltipBorder: isDark ? '#47474e' : '#F2F5F8', // slate-600 / light gray
      tooltipText: isDark ? '#AD46FF' : '#7D7D93', // slate-100 / dark
      tooltipSecondaryText: isDark ? '#7D7D93' : '#656B73', // slate-400 / gray-600
      tooltipDivider: isDark ? '#47474e' : '#F2F5F8', // slate-600 / light gray
      tooltipYearColor: isDark ? '#FFFFFF' : '#141437', // slate-600 / light gray

      // Legend colors
      legendText: isDark ? '#e4e4e7' : '#1a1a1a', // slate-200 / dark
    };
  }

  private observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.renderChart());
    this.resizeObserver.observe(this.chartContainer?.nativeElement);
  }

  private chartContainerWidth = 0;
  private tooltipDiv: any;
  private clearChart() {
    const container = this.chartContainer?.nativeElement;
    if (container) {
      select(container)
        .style('overflow-x', null)
        .style('overflow-y', null);
    }
    select(container).selectAll('*').remove();
    // Also remove any stray body tooltips for this instance
    this.tooltipDiv?.remove();
    this.tooltipDiv = null;
  }

  hiddenIndices = new Set<number>();

  toggleLegend(index: number) {
    if (!this.interactiveLegends) return;
    if (this.hiddenIndices.has(index)) {
      this.hiddenIndices.delete(index);
    } else {
      this.hiddenIndices.add(index);
    }
    this.renderChart();
  }

  // private renderChart(): void {
  //   if (!this.data1?.length) return;

  //   this.clearChart();

  //   const container = this.chartContainer?.nativeElement;
  //   const parent = container?.parentElement?.parentElement;
  //   if (!parent) return;

  //   // Get theme colors
  //   const colors = this.getThemeColors();

  //   const width = parent.clientWidth;
  //   const height = parent.clientHeight;
  //   const maxBarHeight = height * 0.55;
  //   const topPadding = (height - maxBarHeight) / 2;

  //   const allData = [...this.data1, ...this.data2, ...this.data3, ...this.data4];


  //   const maxTotal = max(allData, (d: any) => d[this.valueKey] || 0) || 1;


  //   const yScale = scaleLinear()
  //     .domain([0, maxTotal]) // Add buffer to top
  //     .range([height - this.margin.bottom, topPadding]);


  //   if (this.showDynamicxAxisLabel) {
  //     const xScale = scaleBand()
  //       .domain(this.data1.map((d) => d?.['label']))
  //       .range([0, width - this.margin.right])
  //       .padding(0.2);

  //     const svg = select(container)
  //       .append('svg')
  //       .attr('width', width)
  //       .attr('height', height)
  //       .attr('viewBox', `0 0 ${width} ${height}`)
  //       .attr('xmlns', 'http://www.w3.org/2000/svg')
  //       .attr('version', '1.1')
  //       .style('max-width', '100%')
  //       .style('height', 'auto');

  //     if (this.addGradient) {
  //       this.appendDefs(svg, colors); // Add gradients with theme colors
  //     }
  //     this.combineData(colors); // Combine datasets with theme colors
  //     this.fullBarWidth = xScale.bandwidth();


  //     const tooltip = select(container)
  //       .append('div')
  //       .attr('class', 'tooltip')
  //       .style('opacity', 0)
  //       .style('position', 'absolute')
  //       .style('pointer-events', 'none');

  //     // Draw bars
  //     let ymax = 32;

  //     this.combinedData.forEach((barGroup, i) => {
  //       const groupX = xScale(barGroup[0]?.['label'])!;
  //       // const groupX = xScale(i.toString())!;
  //       const group = svg
  //         .append('g')
  //         .attr('transform', `translate(${this.margin.left}, -${this.margin.bottom})`)
  //         .attr('class', `bar-cluster-${i} bar-cluster`);

  //       // const bgRect = group
  //       //   .append('rect')
  //       //   .attr('x', groupX - 5)
  //       //   .attr('y', 38)
  //       //   .attr('width', this.fullBarWidth + 10.4)
  //       //   .attr('height', yScale(0) - topPadding + 47)
  //       //   .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //       //   .attr('rx', 6)
  //       //   .attr('ry', 6)
  //       //   .style('cursor', 'pointer');


  //       // ✅ Make background box slimmer & centered better
  //       const bgPadding = 3; // reduce space around bars
  //       const bgRect = group
  //         .append('rect')
  //         .attr('x', groupX + bgPadding) // was groupX - 5
  //         .attr('y', this.barClusterHeight || 40)
  //         .attr('width', this.fullBarWidth - bgPadding * 2) // narrower background
  //         .attr('height', yScale(0) - topPadding + this.barClusterPixel)
  //         .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //         .attr('rx', 6)
  //         .attr('ry', 6)
  //         .style('cursor', 'pointer');


  //       group.on('mouseover', () =>
  //         bgRect
  //           .transition()
  //           .duration(200)
  //           .attr('fill', isDark ? '#ffffff' : '#000000')
  //       );
  //       group.on('mouseleave', () =>
  //         bgRect
  //           .transition()
  //           .duration(200)
  //           .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //       );

  //       const groupSize = barGroup.length;
  //       // const innerGap = this.gap + 3;
  //       // const dynamicBarWidth =
  //       //   groupSize >= 3
  //       //     ? this.fullBarWidth / 3 - innerGap
  //       //     : this.fullBarWidth / (groupSize + 1) - innerGap;

  //       const innerGap = this.gap + 4; // increase inner gap slightly
  //       const dynamicBarWidth =
  //         groupSize >= 3
  //           ? this.fullBarWidth / 5.3 - innerGap // reduced width
  //           : this.fullBarWidth / (groupSize + 1.2) - innerGap;


  //       const totalBarsWidth =
  //         groupSize * dynamicBarWidth + (groupSize - 1) * innerGap;
  //       const xOffset = (this.fullBarWidth - totalBarsWidth) / 2;


  //       barGroup.forEach((bar: BarChartData & { color: colorLegend }, j: number) => {
  //         const value = bar[this.valueKey] || 0;
  //         const x = groupX + xOffset + j * (dynamicBarWidth + innerGap);
  //         let y = yScale(value);
  //         let h = yScale(0) - y;

  //         if (value < 5) {
  //           y = y - 3;
  //           h = h + 3;
  //         }

  //         const barPath = this.drawRoundedBar(
  //           x,
  //           y,
  //           dynamicBarWidth,
  //           h,
  //           6,
  //           this.otherKey ? false : true,
  //           this.otherKey ? false : true
  //         );

  //         const subgroup = group.append('g').attr('class', 'bar-group');
  //         if (!this.otherKey) {
  //           subgroup.append('path').attr('d', barPath).attr('fill', bar.color);
  //         }

  //         if (this.otherKey) {
  //           const baseY = yScale(0);
  //           const outlineHeight = baseY - y;

  //           if (ymax > y) ymax = y + 55;

  //           const outlinePath = this.drawRoundedBar(
  //             x + 0.25,
  //             ymax + 30,
  //             dynamicBarWidth,
  //             yScale(0) - topPadding + 18,
  //             6,
  //             true,
  //             true
  //           );

  //           subgroup
  //             .append('path')
  //             .attr('d', outlinePath)
  //             .attr('fill', colors.barOutlineFill)
  //             .attr('stroke', colors.barOutlineStroke)
  //             .attr('stroke-width', 1)
  //             .attr('vector-effect', 'non-scaling-stroke');

  //           subgroup.append('path').attr('d', barPath).attr('fill', bar.color);

  //           subgroup
  //             .append('text')
  //             .attr('x', x + dynamicBarWidth / 2)
  //             .attr('y', 55)
  //             .attr('text-anchor', 'middle')
  //             .attr('fill', colors.barTextColor)
  //             .attr('font-weight', y + h - 20 < y ? '600' : '700')
  //             .attr('font-size', '10px')
  //             .attr('cursor', 'pointer')
  //             .text(bar[this.otherKey] + this.valueKey);
  //         }

  //         subgroup
  //           .append('text')
  //           .attr('x', x + dynamicBarWidth / 2)
  //           .attr('y', this.barTextposition == 'top' ? y - 5 : y + h - 20)
  //           .attr('text-anchor', 'middle')
  //           .attr('fill', y + h - 20 <= y ? colors.barTextColor : colors.barTextColor)
  //           .attr('font-weight', y + h - 20 < y ? '600' : '700')
  //           .attr('font-size', '10px')
  //           .attr('cursor', 'pointer')
  //           .text(value);

  //         subgroup.on('mouseover', () => {
  //           tooltip

  //             .html(this.getToolTipHtml(bar, this.tooltipData, this.datePipe, colors))
  //             .style('opacity', 1);
  //         });




  //         subgroup.on('mousemove', (event: MouseEvent) => {

  //           const [ptrX, ptrY] = pointer(event, container);
  //           const tooltipNode = tooltip.node() as HTMLElement;
  //           const tooltipWidth = tooltipNode.getBoundingClientRect().width;
  //           const tooltipHeight = tooltipNode.getBoundingClientRect().height;

  //           const horizontalOffset = 15;
  //           const verticalOffset = 5;

  //           let tooltipLeft = ptrX + horizontalOffset;
  //           if (tooltipLeft + tooltipWidth > width) {
  //             tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
  //           }

  //           let tooltipTop = ptrY - verticalOffset - tooltipHeight;
  //           if (tooltipTop < 0) tooltipTop = 0;

  //           tooltip
  //             .style('left', `${tooltipLeft}px`)
  //             .style('top', `${tooltipTop}px`);
  //         });

  //         subgroup.on('mouseleave', () => {
  //           tooltip.style('opacity', 0);
  //         });
  //       });
  //     });

  //     this.drawAxes(svg, xScale, yScale, height, colors);
  //     this.appendLegends(svg, height, colors);

  //   }

  //   if (!this.showDynamicxAxisLabel) {
  //     const xScale = scaleBand()
  //       .domain(this.data1.map((_, i) => i.toString()))
  //       .range([0, width - this.margin.right])
  //       .padding(0.2);

  //     const svg = select(container)
  //       .append('svg')
  //       .attr('width', width)
  //       .attr('height', height)
  //       .attr('viewBox', `0 0 ${width} ${height}`)
  //       .attr('xmlns', 'http://www.w3.org/2000/svg')
  //       .attr('version', '1.1')
  //       .style('max-width', '100%')
  //       .style('height', 'auto');

  //     if (this.addGradient) {
  //       this.appendDefs(svg, colors); // Add gradients with theme colors
  //     }
  //     this.combineData(colors); // Combine datasets with theme colors
  //     this.fullBarWidth = xScale.bandwidth();


  //     const tooltip = select(container)
  //       .append('div')
  //       .attr('class', 'tooltip')
  //       .style('opacity', 0)
  //       .style('position', 'absolute')
  //       .style('pointer-events', 'none');

  //     // Draw bars
  //     let ymax = 32;

  //     this.combinedData.forEach((barGroup, i) => {
  //       // const groupX = xScale(barGroup[0]?.['label'])!;
  //       const groupX = xScale(i.toString())!;
  //       const group = svg
  //         .append('g')
  //         .attr('transform', `translate(${this.margin.left}, -${this.margin.bottom})`)
  //         .attr('class', `bar-cluster-${i} bar-cluster`);

  //       // const bgRect = group
  //       //   .append('rect')
  //       //   .attr('x', groupX - 5)
  //       //   .attr('y', 38)
  //       //   .attr('width', this.fullBarWidth + 10.4)
  //       //   .attr('height', yScale(0) - topPadding + 47)
  //       //   .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //       //   .attr('rx', 6)
  //       //   .attr('ry', 6)
  //       //   .style('cursor', 'pointer');


  //       // ✅ Make background box slimmer & centered better
  //       const bgPadding = 3; // reduce space around bars
  //       const bgRect = group
  //         .append('rect')
  //         .attr('x', groupX + bgPadding) // was groupX - 5
  //         .attr('y', 40)
  //         .attr('width', this.fullBarWidth - bgPadding * 2) // narrower background
  //         .attr('height', yScale(0) - topPadding + this.barClusterPixel)
  //         .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //         .attr('rx', 6)
  //         .attr('ry', 6)
  //         .style('cursor', 'pointer');


  //       group.on('mouseover', () =>
  //         bgRect
  //           .transition()
  //           .duration(200)
  //           .attr('fill', isDark ? '#ffffff' : '#000000')
  //       );
  //       group.on('mouseleave', () =>
  //         bgRect
  //           .transition()
  //           .duration(200)
  //           .attr('fill', !this.addGradient ? colors.barOutlineFill : 'url(#gradient-reversed)')
  //       );

  //       const groupSize = barGroup.length;
  //       // const innerGap = this.gap + 3;
  //       // const dynamicBarWidth =
  //       //   groupSize >= 3
  //       //     ? this.fullBarWidth / 3 - innerGap
  //       //     : this.fullBarWidth / (groupSize + 1) - innerGap;

  //       const innerGap = this.gap + 4; // increase inner gap slightly
  //       const dynamicBarWidth =
  //         groupSize >= 3
  //           ? this.fullBarWidth / 3.3 - innerGap // reduced width
  //           : this.fullBarWidth / (groupSize + 1.2) - innerGap;


  //       const totalBarsWidth =
  //         groupSize * dynamicBarWidth + (groupSize - 1) * innerGap;
  //       const xOffset = (this.fullBarWidth - totalBarsWidth) / 2;


  //       barGroup.forEach((bar: BarChartData & { color: colorLegend }, j: number) => {
  //         const value = bar[this.valueKey] || 0;
  //         const x = groupX + xOffset + j * (dynamicBarWidth + innerGap);
  //         let y = yScale(value);
  //         let h = yScale(0) - y;

  //         if (value < 5) {
  //           y = y - 3;
  //           h = h + 3;
  //         }

  //         const barPath = this.drawRoundedBar(
  //           x,
  //           y,
  //           dynamicBarWidth,
  //           h,
  //           6,
  //           this.otherKey ? false : true,
  //           this.otherKey ? false : true
  //         );

  //         const subgroup = group.append('g').attr('class', 'bar-group');
  //         if (!this.otherKey) {
  //           subgroup.append('path').attr('d', barPath).attr('fill', bar.color);
  //         }

  //         if (this.otherKey) {
  //           const baseY = yScale(0);
  //           const outlineHeight = baseY - y;

  //           if (ymax > y) ymax = y + 55;

  //           const outlinePath = this.drawRoundedBar(
  //             x + 0.25,
  //             ymax + 30,
  //             dynamicBarWidth,
  //             yScale(0) - topPadding + 18,
  //             6,
  //             true,
  //             true
  //           );

  //           subgroup
  //             .append('path')
  //             .attr('d', outlinePath)
  //             .attr('fill', colors.barOutlineFill)
  //             .attr('stroke', colors.barOutlineStroke)
  //             .attr('stroke-width', 1)
  //             .attr('vector-effect', 'non-scaling-stroke');

  //           subgroup.append('path').attr('d', barPath).attr('fill', bar.color);

  //           subgroup
  //             .append('text')
  //             .attr('x', x + dynamicBarWidth / 2)
  //             .attr('y', 55)
  //             .attr('text-anchor', 'middle')
  //             .attr('fill', colors.barTextColor)
  //             .attr('font-weight', y + h - 20 < y ? '600' : '700')
  //             .attr('font-size', '10px')
  //             .attr('cursor', 'pointer')
  //             .text(bar[this.otherKey] + this.valueKey);
  //         }

  //         subgroup
  //           .append('text')
  //           .attr('x', x + dynamicBarWidth / 2)
  //           .attr('y', this.barTextposition == 'top' ? y - 5 : y + h - 20)
  //           .attr('text-anchor', 'middle')
  //           .attr('fill', y + h - 20 <= y ? colors.barTextColor : colors.barTextColor)
  //           .attr('font-weight', y + h - 20 < y ? '600' : '700')
  //           .attr('font-size', '10px')
  //           .attr('cursor', 'pointer')
  //           .text(value);

  //         subgroup.on('mouseover', () => {
  //           tooltip

  //             .html(this.getToolTipHtml(bar, this.tooltipData, this.datePipe, colors))
  //             .style('opacity', 1);
  //         });




  //         subgroup.on('mousemove', (event: MouseEvent) => {

  //           const [ptrX, ptrY] = pointer(event, container);
  //           const tooltipNode = tooltip.node() as HTMLElement;
  //           const tooltipWidth = tooltipNode.getBoundingClientRect().width;
  //           const tooltipHeight = tooltipNode.getBoundingClientRect().height;

  //           const horizontalOffset = 15;
  //           const verticalOffset = 5;

  //           let tooltipLeft = ptrX + horizontalOffset;
  //           if (tooltipLeft + tooltipWidth > width) {
  //             tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
  //           }

  //           let tooltipTop = ptrY - verticalOffset - tooltipHeight;
  //           if (tooltipTop < 0) tooltipTop = 0;

  //           tooltip
  //             .style('left', `${tooltipLeft}px`)
  //             .style('top', `${tooltipTop}px`);
  //         });

  //         subgroup.on('mouseleave', () => {
  //           tooltip.style('opacity', 0);
  //         });
  //       });
  //     });

  //     this.drawAxes(svg, xScale, yScale, height, colors);
  //     this.appendLegends(svg, height, colors);

  //   }





  // }


  private renderChart(): void {
    // Require at least data1 (primary series) to render
    if (!this.data1?.length) return;

    // Clear any existing drawing
    this.clearChart();

    const container = this.chartContainer?.nativeElement;
    if (!container) return;

    // Get theme colors
    const colors = this.getThemeColors();

    const rect = container.getBoundingClientRect();
    this.actualWidth = rect.width || container.clientWidth || 600;
    this.actualHeight = rect.height || container.clientHeight || 400;

    const height = this.actualHeight;
    const needsScroll = (this.autoScrollOn5Years && this.has5OrMoreYearsOfData()) || (this.autoScrollOn3Years && this.hasMoreThan3YearsOfData());
    let width = this.actualWidth;
    if (needsScroll) {
      const minWidthPerPoint = 100;
      const calculatedWidth = this.data1.length * minWidthPerPoint + this.margin.left + this.margin.right;
      width = Math.max(width, calculatedWidth);
    }
    const isDark = this.currentTheme === 'dark';
    const maxBarHeight = height * this.barHeightRatio;
    const topPadding = (height - maxBarHeight) / 2;

    // Combine all arrays to compute maximum value (safeguard)
    const allData = [
      ...(Array.isArray(this.data1) ? this.data1 : []),
      ...(Array.isArray(this.data2) ? this.data2 : []),
      ...(Array.isArray(this.data3) ? this.data3 : []),
      ...(Array.isArray(this.data4) ? this.data4 : []),
    ];

    const maxTotal = max(allData, (d: any) => d[this.valueKey] || 0) || 1;
    const yMaxValue = this.yMax > 0 ? this.yMax : maxTotal * 1.15; // Use explicit yMax or 15% headroom

    const chartHeight = height - (this.margin.top || 30) - this.margin.bottom;
    const rangeStart = this.useStandardizedScaling ? chartHeight : height - this.margin.bottom;
    const rangeEnd = this.useStandardizedScaling ? 0 : topPadding;

    const yScale = this.scaleType === 'log'
      ? scaleLog()
        .domain([1, yMaxValue])
        .range([rangeStart, rangeEnd])
        .nice()
      : scaleLinear()
        .domain([0, yMaxValue])
        .range([rangeStart, rangeEnd]);

    // Decide xScale domain & rendering mode
    if (this.showDynamicxAxisLabel) {
      const xScale = scaleBand()
        .domain(this.data1.map((d) => d.label || '').filter(l => !!l))
        .range([this.xScalePadding, width - this.margin.left - this.margin.right])
        .padding(this.groupPadding);

      // Make the container scrollable
      select(container)
        .style('overflow-x', needsScroll ? 'auto' : 'visible')
        .style('overflow-y', 'hidden')
        .style('width', '100%');

      const svg = select(container)
        .append('svg')
        .attr('width', needsScroll ? width : '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('max-width', needsScroll ? 'none' : '100%')
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('xmlns', 'http://www.w3.org/2000/svg');

      if (this.addGradient) {
        this.appendDefs(svg, colors);
      }

      // Build combinedData cluster arrays according to Option C - ONLY for Bars
      this.combineDataForOptionC(colors);

      // Stripe patterns (independent of addGradient)
      if (this.showStripes) {
        this.appendStripeDefs(svg);
      }

      // Filter out datasets that are lines to avoid calculating width for them
      const barDatasetsCount = [this.data1, this.data2, this.data3, this.data4].filter(
        d => Array.isArray(d) && d.length > 0 && d[0].type !== 'line'
      ).length;

      this.fullBarWidth = xScale.bandwidth();

      const tooltip = this.getOrCreateTooltip(colors);

      this.combinedData.forEach((barGroup, i) => {
        const label = barGroup[0]?.label || '';
        const groupX = xScale(label) || 0;
        const baselineY = yScale(this.scaleType === 'log' ? 1 : 0);
        const maxInGroup = max(barGroup, (b: any) => b[this.valueKey] || 0) || 0;
        const groupTopY = yScale(maxInGroup);
        const transformY = this.useStandardizedScaling ? (this.margin.top || 30) : -this.margin.bottom;
        const group = svg
          .append('g')
          .attr('transform', `translate(${this.margin.left}, ${transformY})`)
          .attr('class', `bar-cluster-${i} bar-cluster`);

        // background rectangle (size computed from current fullBarWidth)
        const bgPadding = 3;
        const bgHeight = this.reducedHighlight ? (baselineY - groupTopY) + 10 : (this.useStandardizedScaling ? chartHeight : height - (this.margin.top || 30) - this.margin.bottom);
        const bgY = this.reducedHighlight ? groupTopY - 10 : (this.useStandardizedScaling ? 0 : (this.margin.top || 30));

        const bgRect = group
          .append('rect')
          .attr('x', groupX + bgPadding)
          .attr('y', bgY)
          .attr('width', Math.max(0, this.fullBarWidth - bgPadding * 2))
          .attr('height', Math.max(0, bgHeight))
          .attr('fill', isDark ? '#ffffff' : '#000000')
          .attr('fill-opacity', this.showBgRect ? this.hoverOpacity : 0)
          .attr('rx', 6)
          .attr('ry', 6)
          .style('cursor', 'pointer')
          .style('display', this.showBgRect ? 'block' : 'none');

        group.on('mouseover', () =>
          bgRect.transition().duration(200).attr('fill-opacity', (this.hoverOpacity !== undefined && this.hoverOpacity !== null) ? this.hoverOpacity : (isDark ? 0.15 : 0.1))
        );
        group.on('mouseleave', () =>
          bgRect.transition().duration(200).attr('fill-opacity', (this.hoverOpacity !== undefined && this.hoverOpacity !== null) ? this.hoverOpacity : (isDark ? 0.05 : 0.05))
        );

        // Filter out series that should only render as lines or are hidden
        const barDatasets = barGroup.filter((b: any) => b.type !== 'line' && (!this.interactiveLegends || !this.hiddenIndices.has(b.seriesIndex)));

        const groupSize = barGroup.length;
        const visibleBarCount = barDatasets.length;
        const innerGap = this.gap + this.innerPadding;

        // Adaptive dynamicBarWidth based on visible BAR groupSize
        let dynamicBarWidth = 0;

        if (this.fitBarsToWidth && visibleBarCount > 0) {
          dynamicBarWidth = (this.fullBarWidth - (visibleBarCount - 1) * innerGap) / visibleBarCount;
        } else if (this.barWidthDivisor) {
          dynamicBarWidth = this.fullBarWidth / this.barWidthDivisor - innerGap;
        } else {
          dynamicBarWidth = groupSize >= 3
            ? this.fullBarWidth / 5.3 - innerGap
            : this.fullBarWidth / (Math.max(groupSize, 1) + 1.2) - innerGap;
        }

        if (this.maxBarWidth > 0) {
          dynamicBarWidth = Math.min(dynamicBarWidth, this.maxBarWidth);
        }

        const totalBarsWidth =
          visibleBarCount * dynamicBarWidth + (visibleBarCount - 1) * innerGap;
        const xOffset = (this.fullBarWidth - totalBarsWidth) / 2;

        barDatasets.forEach((bar: any, j: number) => {
          const value = bar[this.valueKey] || 0;
          const x = groupX + xOffset + j * (dynamicBarWidth + innerGap);
          let y = yScale(this.scaleType === 'log' ? Math.max(1, value) : value);
          let h = baselineY - y;

          if (value < 5) {
            y = y - 3;
            h = h + 3;
          }

          const startPath = this.drawRoundedBar(
            x,
            baselineY,
            dynamicBarWidth,
            0,
            this.barRadius,
            this.otherKey ? false : true,
            this.otherKey ? false : true
          );

          const barPath = this.drawRoundedBar(
            x,
            y,
            dynamicBarWidth,
            h,
            this.barRadius,
            this.otherKey ? false : true,
            this.otherKey ? false : true
          );

          const subgroup = group.append('g').attr('class', `bar-group series-${bar.seriesIndex}`);
          if (!this.otherKey) {
            const barFill = this.showStripes
              ? `url(#stripe-${this.chartId}-${(bar.color as string).replace('#', '')})`
              : bar.color;
            if (this.interactiveLegends) {
              subgroup.append('path').attr('d', startPath).attr('fill', barFill)
                .transition().duration(400).attr('d', barPath);
            } else {
              subgroup.append('path').attr('d', barPath).attr('fill', barFill);
            }
          }

          if (this.otherKey) {
            const outlineHeight = baselineY - y;

            const startOutlinePath = this.drawRoundedBar(
              x + 0.25,
              baselineY,
              dynamicBarWidth,
              0,
              6,
              true,
              true
            );

            const outlinePath = this.drawRoundedBar(
              x + 0.25,
              32 + 30,
              dynamicBarWidth,
              baselineY - topPadding + 18,
              6,
              true,
              true
            );

            if (this.interactiveLegends) {
              subgroup
                .append('path')
                .attr('d', startOutlinePath)
                .attr('fill', colors.barOutlineFill)
                .attr('stroke', colors.barOutlineStroke)
                .attr('stroke-width', 1)
                .attr('vector-effect', 'non-scaling-stroke')
                .transition().duration(400).attr('d', outlinePath);

              subgroup.append('path').attr('d', startPath).attr('fill', bar.color)
                .transition().duration(400).attr('d', barPath);
            } else {
              subgroup
                .append('path')
                .attr('d', outlinePath)
                .attr('fill', colors.barOutlineFill)
                .attr('stroke', colors.barOutlineStroke)
                .attr('stroke-width', 1)
                .attr('vector-effect', 'non-scaling-stroke');

              subgroup.append('path').attr('d', barPath).attr('fill', bar.color);
            }
          }

          if (this.showBarValues) {
            subgroup
            subgroup
              .append('text')
              .attr('x', x + dynamicBarWidth / 2)
              .attr('y', this.barTextposition == 'top' ? y - 5 : y + h - 20)
              .attr('text-anchor', 'middle')
              .attr('fill', colors.barTextColor)
              .attr('font-weight', y + h - 20 < y ? '600' : '700')
              .attr('font-size', '10px')
              .attr('cursor', 'pointer')
              .text(this.formatValue(value, this.valueType));
          }

          // tooltip behaviour
          subgroup.on('mouseover', (event) => {
            // Merge sibling series values into the bar object so tooltipData can
            // reference them via _g0, _g1, _g2, etc.
            const mergedBar = { ...bar };
            barGroup.forEach((b: any, idx: number) => { mergedBar[`_g${idx}`] = b[this.valueKey]; });
            tooltip
              .html(this.getToolTipHtml(mergedBar, this.tooltipData, this.datePipe, colors))
              .style('opacity', 1);
          });

          subgroup.on('mousemove', (event: MouseEvent) => {
            this.positionTooltip(tooltip, event);
          });

          subgroup.on('mouseleave', () => {
            tooltip.style('opacity', 0);
          });
        });
      });

      this.drawLines(svg, xScale, yScale, colors, tooltip); // Draw Line Charts on top
      this.drawAxes(svg, xScale, yScale, height, width, colors);

      if (this.showLegends) {
        this.appendLegends(svg, height, colors);
      }
    } else {
      // Non-dynamic labels (index-based)
      const xScale = scaleBand()
        .domain(this.data1.map((_, i) => i.toString()))
        .range([this.xScalePadding, width - this.margin.left - this.margin.right])
        .padding(this.groupPadding);

      // Make the container scrollable
      select(container)
        .style('overflow-x', needsScroll ? 'auto' : 'visible')
        .style('overflow-y', 'hidden')
        .style('width', '100%');

      const svg = select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('xmlns', 'http://www.w3.org/2000/svg')
        .style('max-width', needsScroll ? 'none' : '100%')
        .style('height', 'auto');

      if (this.addGradient) {
        this.appendDefs(svg, colors);
      }

      this.combineDataForOptionC(colors); // use same combining logic

      // Stripe patterns (independent of addGradient)
      if (this.showStripes) {
        this.appendStripeDefs(svg);
      }
      this.fullBarWidth = xScale.bandwidth();

      // Tooltip appended to BODY
      const tooltip = select('body')
        .append('div')
        .attr('class', 'multiple-bar-tooltip')
        .style('opacity', 0)
        .style('position', 'fixed') // Fixed is more robust
        .style('pointer-events', 'none')
        .style('z-index', '9999');

      this.combinedData.forEach((barGroup, i) => {
        const groupX = xScale(i.toString())!;
        const baselineY = yScale(this.scaleType === 'log' ? 1 : 0);
        const maxInGroup = max(barGroup, (b: any) => b[this.valueKey] || 0) || 0;
        const groupTopY = yScale(maxInGroup);
        const group = svg
          .append('g')
          .attr('transform', `translate(${this.margin.left}, -${this.margin.bottom})`)
          .attr('class', `bar-cluster-${i} bar-cluster`);

        const bgPadding = 3;
        const bgHeight = this.reducedHighlight ? (baselineY - groupTopY) + 10 : height - (this.margin.top || 30) - this.margin.bottom;
        const bgY = this.reducedHighlight ? groupTopY - 10 : (this.margin.top || 30);

        const bgRect = group
          .append('rect')
          .attr('x', groupX + bgPadding)
          .attr('y', bgY)
          .attr('width', Math.max(0, this.fullBarWidth - bgPadding * 2))
          .attr('height', Math.max(0, bgHeight))
          .attr('fill', isDark ? '#ffffff' : '#000000')
          .attr('fill-opacity', this.showBgRect ? this.hoverOpacity : 0)
          .attr('rx', 6)
          .attr('ry', 6)
          .style('cursor', 'pointer')
          .style('display', this.showBgRect ? 'block' : 'none');

        group.on('mouseover', () =>
          bgRect.transition().duration(200).attr('fill-opacity', (this.hoverOpacity !== undefined && this.hoverOpacity !== null) ? this.hoverOpacity : (isDark ? 0.15 : 0.1))
        );
        group.on('mouseleave', () =>
          bgRect.transition().duration(200).attr('fill-opacity', (this.hoverOpacity !== undefined && this.hoverOpacity !== null) ? this.hoverOpacity : (isDark ? 0.05 : 0.05))
        );
        group.on('mouseout', () =>
          bgRect.transition().duration(200).attr('fill-opacity', (this.hoverOpacity !== undefined && this.hoverOpacity !== null) ? this.hoverOpacity : (isDark ? 0.05 : 0.05))
        );

        // Filter out series that should only render as lines or are hidden
        const barDatasets = barGroup.filter((b: any) => b.type !== 'line' && (!this.interactiveLegends || !this.hiddenIndices.has(b.seriesIndex)));

        const groupSize = barGroup.length;
        const visibleBarCount = barDatasets.length;
        const innerGap = this.gap + this.innerPadding;

        let dynamicBarWidth = 0;
        if (this.fitBarsToWidth && visibleBarCount > 0) {
          dynamicBarWidth = (this.fullBarWidth - (visibleBarCount - 1) * innerGap) / visibleBarCount;
        } else if (this.barWidthDivisor) {
          dynamicBarWidth = this.fullBarWidth / this.barWidthDivisor - innerGap;
        } else {
          dynamicBarWidth = groupSize >= 3
            ? this.fullBarWidth / 3.3 - innerGap
            : this.fullBarWidth / (groupSize + 1.2) - innerGap;
        }

        if (this.maxBarWidth > 0) {
          dynamicBarWidth = Math.min(dynamicBarWidth, this.maxBarWidth);
        }

        const totalBarsWidth =
          visibleBarCount * dynamicBarWidth + (visibleBarCount - 1) * innerGap;
        const xOffset = (this.fullBarWidth - totalBarsWidth) / 2;

        barDatasets.forEach((bar: any, j: number) => {
          const value = bar[this.valueKey] || 0;
          const x = groupX + xOffset + j * (dynamicBarWidth + innerGap);
          let y = yScale(this.scaleType === 'log' ? Math.max(1, value) : value);
          let h = baselineY - y;

          if (value < 5) {
            y = y - 3;
            h = h + 3;
          }

          const startPath = this.drawRoundedBar(
            x,
            baselineY,
            dynamicBarWidth,
            0,
            6,
            this.otherKey ? false : true,
            this.otherKey ? false : true
          );

          const barPath = this.drawRoundedBar(
            x,
            y,
            dynamicBarWidth,
            h,
            6,
            this.otherKey ? false : true,
            this.otherKey ? false : true
          );

          const subgroup = group.append('g').attr('class', `bar-group series-${bar.seriesIndex}`);
          if (!this.otherKey) {
            const barFill = this.showStripes
              ? `url(#stripe-${this.chartId}-${(bar.color as string).replace('#', '')})`
              : bar.color;
            if (this.interactiveLegends) {
              subgroup.append('path').attr('d', startPath).attr('fill', barFill)
                .transition().duration(400).attr('d', barPath);
            } else {
              subgroup.append('path').attr('d', barPath).attr('fill', barFill);
            }
          }

          if (this.showBarValues) {
            subgroup
              .append('text')
              .attr('x', x + dynamicBarWidth / 2)
              .attr('y', this.barTextposition == 'top' ? y - 5 : y + h - 20)
              .attr('text-anchor', 'middle')
              .attr('fill', colors.barTextColor)
              .attr('font-weight', y + h - 20 < y ? '600' : '700')
              .attr('font-size', '10px')
              .attr('cursor', 'pointer')
              .text(this.formatValue(value, this.valueType));
          }

          subgroup.on('mouseover', () => {
            const mergedBar = { ...bar };
            barGroup.forEach((b: any, idx: number) => { mergedBar[`_g${idx}`] = b[this.valueKey]; });
            tooltip
              .html(this.getToolTipHtml(mergedBar, this.tooltipData, this.datePipe, colors))
              .style('opacity', 1);
          });

          subgroup.on('mousemove', (event: MouseEvent) => {
            this.positionTooltip(tooltip, event);
          });

          subgroup.on('mouseleave', () => {
            tooltip.style('opacity', 0);
          });
        });
      });

      this.drawAxes(svg, xScale as any, yScale as any, height, width, colors);
      this.appendLegends(svg, height, colors);
    }
  }


  private drawLines(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    xScale: ScaleBand<string>,
    yScale: any,
    colors: ReturnType<typeof this.getThemeColors>,
    tooltip: any
  ) {
    const lineDatasets = [
      { data: this.data1, color: colors.primaryBar, key: 'data1', seriesIndex: 0 },
      { data: this.data2, color: colors.secondaryBar, key: 'data2', seriesIndex: 1 },
      { data: this.data3, color: colors.tertiaryBar, key: 'data3', seriesIndex: 2 },
      { data: this.data4, color: colors.forthBar, key: 'data4', seriesIndex: 3 }
    ].filter(d => Array.isArray(d.data) && d.data.length > 0 && d.data[0].type === 'line' && (!this.interactiveLegends || !this.hiddenIndices.has(d.seriesIndex)));

    const lineGenerator = line<BarChartData>()
      .x((d) => (xScale(d.label || '') || 0) + xScale.bandwidth() / 2)
      .y((d) => yScale(this.scaleType === 'log' ? Math.max(1, d[this.valueKey] || 0) : d[this.valueKey] || 0))
      .curve(curveMonotoneX);

    const lineGroup = svg.append('g')
      .attr('transform', `translate(${this.margin.left}, -${this.margin.bottom})`)
      .style('pointer-events', 'none');

    lineDatasets.forEach(ds => {
      // Draw Line
      const pathSel = lineGroup.append('path')
        .datum(ds.data)
        .attr('class', `series-${ds.seriesIndex}`)
        .attr('fill', 'none')
        .attr('stroke', ds.color)
        .attr('stroke-width', 3)
        .attr('d', lineGenerator as any);

      if (this.interactiveLegends) {
        pathSel.style('opacity', 0)
          .transition().duration(400)
          .style('opacity', 1);
      }

      if (this.interactiveLegends) {
        pathSel.style('opacity', 0)
          .transition().duration(400)
          .style('opacity', 1);
      }

      if (this.interactiveLegends) {
        pathSel.style('opacity', 0)
          .transition().duration(400)
          .style('opacity', 1);
      }

      // Draw Points
      const pointsGroup = lineGroup.append('g')
        .attr('class', `series-${ds.seriesIndex}`);

      const circles = pointsGroup.selectAll(`.dot-${ds.key}`)
        .data(ds.data)
        .enter()
        .append('circle')
        .attr('class', `dot-${ds.key}`)
        .attr('cx', (d) => (xScale(d.label || '') || 0) + xScale.bandwidth() / 2)
        .attr('cy', (d) => yScale(this.scaleType === 'log' ? Math.max(1, d[this.valueKey] || 0) : d[this.valueKey] || 0))
        .attr('r', 5)
        .attr('fill', '#fff')
        .attr('stroke', ds.color)
        .attr('stroke-width', 2)
        .style('pointer-events', 'all')
        .style('cursor', 'pointer')
        .on('mouseover', (event, d) => {
          select(event.currentTarget).attr('r', 7);
          tooltip
            .html(this.getToolTipHtml(d, this.tooltipData, this.datePipe, colors))
            .style('opacity', 1);
        })
        .on('mousemove', (event: MouseEvent) => {
          this.positionTooltip(tooltip, event);
        })
        .on('mouseleave', (event) => {
          select(event.currentTarget).attr('r', 5);
          tooltip.style('opacity', 0);
        });

      if (this.interactiveLegends) {
        circles.style('opacity', 0)
          .transition().duration(400)
          .style('opacity', 1);
      }
    });
  }

  /**
   * Option C (extended):
   *  - data1, data2, data3 → MAIN datasets (mandatory)
   *  - data4, data5, data6... → OPTIONAL datasets
   *
   *  All datasets align to data1.labels
   */
  private combineDataForOptionC(colors: ReturnType<typeof this.getThemeColors>) {
    // Collect datasets dynamically
    const datasets: any[][] = [];

    // Push first 3 main datasets
    if (Array.isArray(this.data1)) datasets.push(this.data1);
    if (Array.isArray(this.data2)) datasets.push(this.data2);
    if (Array.isArray(this.data3)) datasets.push(this.data3);

    // Push any additional optional datasets
    const optionalSets = [
      this.data4,

    ];

    optionalSets.forEach(set => {
      if (Array.isArray(set) && set.length > 0) {
        datasets.push(set);
      }
    });

    // Build a color palette big enough for unlimited datasets
    const palette = [
      colors.primaryBar,
      colors.secondaryBar,
      colors.tertiaryBar,
      colors.forthBar,
      "#9B5CF6",
      "#14D18E",
      "#FFD76B",
      "#3DD9C0",
      "#F48FB1",
      "#7986CB",
    ];

    // Map all datasets by label
    const maps = datasets.map(set => {
      const m = new Map<string, any>();
      set.forEach(item => {
        if (item?.label) m.set(String(item.label), item);
      });
      return m;
    });

    const mainLabels = this.data1.map(d => d.label || '').filter(l => !!l);

    // Build final cluster structure
    this.combinedData = mainLabels.map((label, index) => {
      const cluster: any[] = [];

      datasets.forEach((set, setIndex) => {
        let found;
        if (setIndex === 0) {
          found = maps[setIndex].get(label) || set[index];
        } else {
          // For data2, data3 etc, assume they are already sorted/aligned by index
          // This prevents "Ghost Matches" on placeholders
          found = set[index];
        }

        if (found) {
          cluster.push({
            ...found,
            seriesIndex: setIndex,
            // Don't override the label - use the original label from the data
            // Overriding causes values from different dates to be shown together
            color: palette[setIndex % palette.length],
          });
        }
      });

      return cluster;
    });



  }


  private appendDefs(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const defs = svg.append('defs');

    const createGradient = (id: string, stops: [string, string][]) => {
      const gradient = defs
        .append('linearGradient')
        .attr('id', id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      stops.forEach(([offset, color]) => {
        gradient
          .append('stop')
          .attr('offset', offset)
          .attr('stop-color', color);
      });
    };

    createGradient('gradient-normal', [
      ['0%', colors.gradientNormalStart],
      ['100%', colors.gradientNormalEnd],
    ]);
    createGradient('gradient-reversed', [
      ['0%', colors.gradientReversedStart],
      ['100%', colors.gradientReversedEnd],
    ]);
  }

  private appendStripeDefs(svg: Selection<SVGSVGElement, unknown, null, undefined>) {
    // Ensure defs element exists (may have been created by appendDefs)
    let defs = svg.select<SVGDefsElement>('defs');
    if (defs.empty()) {
      defs = svg.append('defs') as any;
    }

    const allColors = new Set<string>();
    this.combinedData.forEach(cluster => {
      cluster.forEach((bar: any) => {
        if (bar.color) allColors.add(bar.color);
      });
    });

    allColors.forEach(color => {
      const patternId = `stripe-${this.chartId}-${color.replace('#', '')}`;
      const pattern = defs.append('pattern')
        .attr('id', patternId)
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 8)
        .attr('height', 8)
        .attr('patternTransform', 'rotate(-45)');

      pattern.append('rect')
        .attr('width', 8)
        .attr('height', 8)
        .attr('fill', color)
        .attr('fill-opacity', 1);

      pattern.append('line')
        .attr('x1', 0).attr('y1', 0)
        .attr('x2', 0).attr('y2', 8)
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 4);
    });
  }

  private combineData(colors: ReturnType<typeof this.getThemeColors>) {
    const maxLength = Math.max(
      this.data1.length,
      this.data2.length,
      this.data3.length,
      this.data4.length
    );
    this.combinedData = [];

    for (let i = 0; i < maxLength; i++) {
      let data: (BarChartData & { color: colorLegend })[] = []

      if (this.data1.length && this.data1[i]) { // Added check for data existence
        data.push({ ...this.data1[i], seriesIndex: 0, color: (colors.primaryBar as colorLegend) });
      }
      if (this.data2.length && this.data2[i]) {
        data.push({ ...this.data2[i], seriesIndex: 1, color: (colors.secondaryBar as colorLegend) });
      }
      if (this.data3.length && this.data3[i]) {
        data.push({ ...this.data3[i], seriesIndex: 2, color: (colors.tertiaryBar as colorLegend) });
      }
      if (this.data4.length && this.data4[i]) {
        data.push({ ...this.data4[i], seriesIndex: 3, color: (colors.forthBar as colorLegend) });
      }

      this.combinedData.push(data);
    }
  }

  private drawAxes(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,

    x: ScaleBand<string>,
    y: any,
    height: number,
    width: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const tickLabels = this.combinedData.map((item: any) =>
      Array.isArray(item)
        ? item[0].month_short
          ? item[0].month_short
          : this.labelKey == 'week'
            ? this.getWeeksBetween(item[0].start, item[0].end)
            : dayjs(item[0].start).format('MMM YYYY')
        : typeof item == 'object'
          ? item.month_short
            ? item.month_short
            : this.labelKey == 'week'
              ? this.getWeeksBetween(item.start, item.end)
              : dayjs(item.start).format('MMM YYYY')
          : ''
    );


    if (!this.showDynamicxAxisLabel) {

      const xAxis = axisBottom(x).tickSize(0);

      if (this.xAxisShowOnlyFirstLast) {
        const domain = x.domain();
        if (this.showAllXticks) {
          xAxis.tickValues(domain);
        } else if (domain.length > 1) {
          xAxis.tickValues([domain[0], domain[domain.length - 1]]);
        } else {
          xAxis.tickValues(domain);
        }
      } else {
        xAxis.tickValues(x.domain());
      }

      xAxis.tickFormat((d, i) => {
        const index = parseInt(d as string);
        return !isNaN(index) ? tickLabels[index] ?? '' : tickLabels[i] ?? '';
      });

      const chartHeight = height - (this.margin.top || 30) - this.margin.bottom;
      const transformY = this.useStandardizedScaling ? (chartHeight + (this.margin.top || 30)) : (height - 2 * this.margin.bottom);
      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${this.margin.left}, ${transformY})`)
        .call(xAxis);


      xAxisGroup.select('path.domain').remove();

      const tickTextSel = xAxisGroup
        .selectAll('g.tick text')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', colors.axisText)
        .style('font-size', this.xAxisFontSize)
        .style('font-weight', 500);

      // When only the first/last labels are shown, the band-center anchor
      // makes long labels (e.g. "Q4 2016 - Q4 2016" for the quarterly + all-time
      // filter) spill over both edges of the parent card which clips them via
      // overflow:hidden. Anchor the first tick to "start" and the last to
      // "end" so the labels grow inward and stay fully visible.
      if (this.xAxisShowOnlyFirstLast && !this.showAllXticks) {
        const tickCount = tickTextSel.size();
        tickTextSel.each(function (_d, i) {
          const anchor = i === 0 ? 'start' : i === tickCount - 1 ? 'end' : 'middle';
          select(this as SVGTextElement).attr('text-anchor', anchor);
        });
      }

      const innerWidth = width - this.margin.left - this.margin.right;

      xAxisGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);

    }

    if (this.showDynamicxAxisLabel) {

      const xAxis = axisBottom(x).tickSize(0);

      if (this.xAxisShowOnlyFirstLast) {
        const domain = x.domain();
        if (this.showAllXticks) {
          xAxis.tickValues(domain);
        } else if (domain.length > 1) {
          xAxis.tickValues([domain[0], domain[domain.length - 1]]);
        } else {
          xAxis.tickValues(domain);
        }
      } else {
        xAxis.tickValues(x.domain());
      }

      xAxis.tickFormat((label) => label);

      const chartHeight = height - (this.margin.top || 30) - this.margin.bottom;
      const transformY = this.useStandardizedScaling ? (chartHeight + (this.margin.top || 30)) : (height - 2 * this.margin.bottom);
      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${this.margin.left}, ${transformY})`)
        .call(xAxis);

      xAxisGroup.select('path.domain').remove();

      const tickTextSel = xAxisGroup
        .selectAll('g.tick text')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', colors.axisText)
        .style('font-size', this.xAxisFontSize)
        .style('font-weight', 500);

      // Same edge-clipping fix as the index-based path above: pin the first
      // label to start-anchor and the last label to end-anchor so long
      // dynamic labels (e.g. "Q2 2026 - Q2 2026") don't bleed past the card.
      if (this.xAxisShowOnlyFirstLast && !this.showAllXticks) {
        const tickCount = tickTextSel.size();
        tickTextSel.each(function (_d, i) {
          const anchor = i === 0 ? 'start' : i === tickCount - 1 ? 'end' : 'middle';
          select(this as SVGTextElement).attr('text-anchor', anchor);
        });
      }

      const innerWidth = width - this.margin.left - this.margin.right;

      xAxisGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);

    }




    if (this.showYAxis) {
      const baselineY = y(this.scaleType === 'log' ? 1 : 0);
      const innerWidth = width - this.margin.left - this.margin.right;

      const yAxisTicks = this.scaleType === 'log' ? 5 : 5;
      const allTicks = y.ticks(yAxisTicks);

      // Filter for major ticks (powers of 10) on log scale, or all on linear
      const labeledTicks = this.scaleType === 'log'
        ? allTicks.filter((d: any) => {
          const log = Math.log10(d);
          return Math.abs(log - Math.round(log)) < 1e-10;
        })
        : allTicks;

      // Draw horizontal grid lines (ONLY for labeled values)
      const gridAxis = axisLeft(y)
        .tickValues(labeledTicks)
        .tickSize(-innerWidth)
        .tickFormat(() => '');

      const gridTransformY = this.useStandardizedScaling ? (this.margin.top || 30) : (height - 2 * this.margin.bottom - baselineY);
      const gridGroup = svg.insert('g', ':first-child')
        .attr('class', 'grid')
        .attr('transform', `translate(${this.margin.left}, ${gridTransformY})`)
        .style('display', this.showGrid ? 'block' : 'none')
        .call(gridAxis);

      gridGroup.selectAll('line')
        .attr('stroke', colors.gridLine)
        .attr('stroke-opacity', 1)
        .attr('stroke-dasharray', '4,4');

      gridGroup.select('path.domain').remove();

      // Draw Y-Axis Labels
      const yAxis = axisLeft(y)
        .tickValues(labeledTicks)
        .tickSize(0)
        .tickPadding(10)
        .tickFormat((d: any) => {
          const val = Number(d);
          let formatted = val.toString();
          if (val >= 1000000) formatted = (val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 1) + 'M';
          else if (val >= 1000) formatted = (val / 1000).toFixed(val % 1000 === 0 ? 0 : 1) + 'K';

          if (this.valueType === '%') return `${formatted}%`;
          if (this.valueType === '$') return `$${formatted}`;
          return formatted;
        });

      if (this.yAxisShowOnlyFirstLast) {
        const yTicks = y.ticks();
        if (this.showAllYticks) {
          yAxis.tickValues(yTicks);
        } else if (yTicks && yTicks.length > 1) {
          yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
        }
      }

      const yAxisTransformY = this.useStandardizedScaling ? (this.margin.top || 30) : (height - 2 * this.margin.bottom - baselineY);
      const yAxisGroup = svg.append('g')
        .attr('transform', `translate(${this.margin.left}, ${yAxisTransformY})`)
        .call(yAxis);

      yAxisGroup.selectAll('text')
        .attr('fill', colors.axisText)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');

      yAxisGroup.selectAll('path.domain').remove();

      // Vertical line for Y-Axis
      const [yStart, yEnd] = y.range();
      yAxisGroup.append('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', yStart)
        .attr('y2', yEnd)
        .attr('stroke', colors.axisLine || '#000')
        .attr('stroke-width', 2)
        .attr('shape-rendering', 'crispEdges');
    }
  }

  private drawRoundedBar(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    roundTopLeft = false,
    roundTopRight = false
  ): string {
    const right = x + width;
    const bottom = y + height;

    return [
      `M${x},${bottom}`,
      `V${y + (roundTopLeft ? radius : 0)}`,
      roundTopLeft
        ? `a${radius},${radius} 0 0 1 ${radius},-${radius}`
        : `V${y}`,
      `H${right - (roundTopRight ? radius : 0)}`,
      roundTopRight
        ? `a${radius},${radius} 0 0 1 ${radius},${radius}`
        : `H${right}`,
      `V${bottom}`,
      `H${x}`,
      `Z`,
    ].join(' ');
  }

  appendLegends(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    height: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    if (!this.legendData || this.legendData.length === 0) {
      return;
    }

    const itemPadding = 10; // Extra padding at the end of each item
    const circleRadius = 5;
    const fontSize = 12;

    // Create a temporary group to measure text widths
    const tempGroup = svg.append('g').style('visibility', 'hidden');
    const measuredItems = this.legendData.map((d, i) => {
      const textToMeasure = tempGroup.append('text').style('font-size', `${fontSize}px`).text(d.label);
      const textWidth = (textToMeasure.node() as SVGTextElement).getComputedTextLength() || 0;

      // Calculate minimum width required to show circle + text + padding
      const minRequiredWidth = circleRadius * 2 + 8 + textWidth + itemPadding;

      return {
        ...d,
        // Use legendGap if it's large enough, otherwise expand to fit content
        width: Math.max(minRequiredWidth, this.legendGap),
        color: d.color || (i === 0 ? colors.primaryBar : i === 1 ? colors.secondaryBar : colors.tertiaryBar)
      };
    });
    tempGroup.remove();

    const totalLegendWidth = measuredItems.reduce((acc, item) => acc + item.width, 0);

    const x0 = this.margin.left;
    const containerWidth = this.chartContainer?.nativeElement?.clientWidth || 0;

    // Calculate legend X position
    let legendX = x0;
    if (this.legendPosition === 'center') {
      legendX = (containerWidth - totalLegendWidth) / 2;
    } else if (this.legendPosition === 'right') {
      legendX = containerWidth - totalLegendWidth - this.margin.right;
    }

    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendX}, ${height - 15})`);

    let currentX = 0;
    measuredItems.forEach((d, i) => {
      const g = legend.append('g')
        .attr('class', 'legend-item')
        .attr('transform', `translate(${currentX}, 0)`);

      if (this.interactiveLegends) {
        g.style('cursor', 'pointer')
          .style('opacity', this.hiddenIndices.has(i) ? 0.4 : 1)
          .on('click', () => this.toggleLegend(i));
      }

      if (d.type === 'square' || this.legendIconType === 'square') {
        g.append('rect')
          .attr('x', 0)
          .attr('y', 6 - circleRadius)
          .attr('width', circleRadius * 2)
          .attr('height', circleRadius * 2)
          .attr('rx', 2) // slight rounding for a nicer square
          .attr('fill', d.color as string);
      } else {
        g.append('circle')
          .attr('cx', circleRadius)
          .attr('cy', 6)
          .attr('r', circleRadius)
          .attr('fill', d.color as string);
      }

      g.append('text')
        .attr('x', circleRadius + 8)
        .attr('y', 10)
        .text(d.label)
        .style('font-size', `${fontSize}px`)
        .style('fill', colors.legendText);

      currentX += d.width;
    });
  }

  getOffsetValues(svg: any) {
    const bars = svg.selectAll('.bar-cluster');
    let x0 = 0,
      x1 = 0;
    if (!bars.empty()) {
      const firstBar = bars.nodes()[0];
      const lastBar = bars.nodes()[bars.size() - 1];

      x0 = parseFloat(select(firstBar).select('rect').attr('x') || '0');
      x1 =
        parseFloat(select(lastBar).select('rect').attr('x') || '0') +
        parseFloat(select(lastBar).select('rect').attr('width') || '0');
    }

    return [x0, x1];
  }

  // getToolTipHtml(
  //   bar: BarChartData & { color: colorLegend },
  //   tooltipData: TooltipConfig[],
  //   datePipe: DatePipe,
  //   colors: ReturnType<typeof this.getThemeColors>
  // ) {
  //   let html = `<div class="w-[150px] p-2 rounded shadow-sm" style="
  //     background-color: ${colors.tooltipBg};
  //     border: 1px solid ${colors.tooltipBorder};
  //     padding: 0.5rem !important;
  //   ">
  //             <div style="border-bottom: 2px solid ${colors.tooltipDivider};">
  //               <span class="w-2 inline-block h-2 rounded-full mr-1" style="background-color:${bar.color
  //     }"></span>
  //               <span style="color: ${colors.tooltipText};">${datePipe.transform(bar.start, 'yyyy')}</span>
  //             </div>`;

  //   tooltipData.forEach((item: any) => {
  //     if (bar[item.key] || bar[item.key] === 0) {
  //       html += `<div class="text-sm pt-1 flex items-center justify-between">
  //                             <span class="pr-2" style="color: ${colors.tooltipSecondaryText};">${item.label}</span>
  //                             <span class="font-normal text-right" style="color: ${colors.tooltipText};">${item.key === this.valueKey
  //           ? bar[item.key] + this.valueKey
  //           : bar[item.key]
  //         }</span>
  //                           </div>`;
  //     }
  //   });

  //   return html + `</div>`;
  // }

  getToolTipHtml(
    bar: any,
    tooltipData: any,
    datePipe: DatePipe,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const year = bar?.label || '';
    // Determine if we should show breakdown (if bar has tooltipData keys) or comparison
    const hasBreakdownKeys = this.tooltipData?.some(config => bar[config.key] !== undefined);
    const rows = hasBreakdownKeys ? this.getBreakdownRows(bar) : this.getTooltipRows(bar);

    let html = `
    <div style="
      background:${colors.tooltipBg};
      border:1px solid ${colors.tooltipBorder};
      padding:16px;
      border-radius:16px;
      min-width:200px;
      box-shadow:0px 12px 32px rgba(0,0,0,0.25);
      font-family: Inter, sans-serif;
    ">
      <div style="
        color:${colors.tooltipYearColor};
        font-weight:600;
        margin-bottom:10px;
        font-size:14px;
        border-bottom:1px solid ${colors.tooltipDivider};
        padding-bottom:8px;
        display: flex;
        align-items: center;
        gap: 8px;
      ">
        <div style="width:8px; height:8px; border-radius:50%; background:${bar.color || colors.primaryBar};"></div>
        ${year}
      </div>
      <div style="display:flex; flex-direction:column; gap:8px;">
    `;

    rows.forEach(row => {
      html += `
        <div style="display:flex; justify-content:space-between; align-items:center; gap:12px;">
          <span style="color:${colors.tooltipSecondaryText}; font-size:13px;">${row.label}</span>
          <span style="color:${colors.tooltipText}; font-size:13px; font-weight:700;">${row.value}</span>
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  }

  private getBreakdownRows(bar: any) {
    const rows: any[] = [];
    this.tooltipData.forEach(config => {
      const val = bar[config.key];
      if (val !== undefined && val !== null) {
        rows.push({
          label: config.label,
          value: this.formatValue(val, config.type || this.valueType)
        });
      }
    });
    return rows;
  }

  private formatValue(val: any, type?: string) {
    const numValue = Number(val);
    if (isNaN(numValue)) return val;

    const formattedNumber = numValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    if (type === '$') return `$${formattedNumber}`;
    if (type === 'percent' || type === '%') return `${formattedNumber}%`;
    return formattedNumber;
  }

  //   getLabelForBar(barKey: string, metric: string) {
  //   const isPrev = barKey.toLowerCase().includes("previous");

  //   const labels = {
  //     contractCreated: isPrev ? "Previous Contract Created:" : "Contract Created:",
  //     registeredBusinesses: isPrev ? "Previous Registered Businesses:" : "Registered Businesses:",
  //   };

  //   return labels[metric];
  // }


  private getTooltipRows(bar: any) {
    const rows: any[] = [];
    const barDate = bar.start || bar['date'] || bar['label'];

    [this.data1, this.data2, this.data3, this.data4].forEach((series, i) => {
      if (!series || !Array.isArray(series)) return;

      const match = series.find(d => (d.start || d['date'] || d['label']) === barDate);
      if (match) {
        const val = match?.['value'] ?? match?.[this.valueKey];
        let displayValue = "-";

        if (val !== undefined && val !== null && !isNaN(val)) {
          displayValue = this.formatValue(val, this.valueType);
        }

        rows.push({
          label: match?.['key'] || this.getSeriesLabel(i),
          value: displayValue,
          color: this.getSeriesColor(i)
        });
      }
    });

    return rows;
  }

  private getSeriesLabel(index: number): string {
    if (this.legendData && this.legendData[index]) {
      return this.legendData[index].label;
    }
    return `Series ${index + 1}`;
  }

  private getSeriesColor(index: number): string {
    const colors = this.getThemeColors();
    const palette = [colors.primaryBar, colors.secondaryBar, colors.tertiaryBar, colors.forthBar];
    return palette[index] || '#ccc';
  }

  // Positions the tooltip so it never gets clipped by the viewport edges.
  // Mobile screens are narrow, and bars near the right/left edge of a
  // horizontally-scrollable chart container can place the pointer close to a
  // viewport edge. Without clamping, the tooltip would render off-screen.
  private positionTooltip(tooltip: any, event: MouseEvent | { clientX: number; clientY: number }) {
    const ptrX = event.clientX;
    const ptrY = event.clientY;

    const tooltipNode = tooltip.node() as HTMLElement | null;
    if (!tooltipNode) return;

    const rect = tooltipNode.getBoundingClientRect();
    const tooltipWidth = rect.width;
    const tooltipHeight = rect.height;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const edgePadding = 8; // gap from viewport edges
    const pointerGap = 15;

    const container = this.chartContainer?.nativeElement;
    
    let rightBoundary = viewportWidth;
    let currentElement = container;
    while (currentElement && currentElement !== document.body) {
      const style = window.getComputedStyle(currentElement);
      if (['auto', 'scroll', 'hidden', 'clip'].includes(style.overflowX) || ['auto', 'scroll', 'hidden', 'clip'].includes(style.overflow)) {
        const rect = currentElement.getBoundingClientRect();
        rightBoundary = Math.min(rightBoundary, rect.right);
      }
      currentElement = currentElement.parentElement;
    }

    // Horizontal placement: try right of pointer, fall back to left, then clamp.
    let tooltipLeft = ptrX + pointerGap;
    if (tooltipLeft + tooltipWidth > rightBoundary - edgePadding) {
      tooltipLeft = ptrX - pointerGap - tooltipWidth;
    }
    if (tooltipLeft < edgePadding) {
      tooltipLeft = edgePadding;
    }
    if (tooltipLeft + tooltipWidth > viewportWidth - edgePadding) {
      tooltipLeft = Math.max(edgePadding, viewportWidth - tooltipWidth - edgePadding);
    }

    // Vertical placement: try above pointer, fall back to below, then clamp.
    let tooltipTop = ptrY - 5 - tooltipHeight;
    if (tooltipTop < edgePadding) {
      tooltipTop = ptrY + pointerGap;
    }
    if (tooltipTop + tooltipHeight > viewportHeight - edgePadding) {
      tooltipTop = Math.max(edgePadding, viewportHeight - tooltipHeight - edgePadding);
    }

    tooltip
      .style('left', `${tooltipLeft}px`)
      .style('top', `${tooltipTop}px`);
  }

  private getOrCreateTooltip(colors: any) {
    if (!this.tooltipDiv || this.tooltipDiv.empty()) {
      this.tooltipDiv = select('body')
        .append('div')
        .attr('class', 'multiple-bar-tooltip')
        .style('opacity', 0)
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('z-index', '9999')
        .style('background', colors.tooltipBg)
        .style('border', `1px solid ${colors.tooltipBorder}`)
        .style('border-radius', '12px')
        .style('padding', '0') // Padding handled in getToolTipHtml for cleaner look
        .style('box-shadow', '0 10px 25px rgba(0,0,0,0.2)')
        .style('transition', 'opacity 0.2s ease');
    } else {
      // Update existing tooltip styles for theme changes
      this.tooltipDiv
        .style('background', colors.tooltipBg)
        .style('border', `1px solid ${colors.tooltipBorder}`);
    }
    return this.tooltipDiv;
  }


  getWeeksBetween(startDate: any, endDate: any) {
    const year = dayjs(startDate).year();
    const start = dayjs(startDate).isoWeekday(1);
    const end = dayjs(endDate).isoWeekday(7);

    const weeks: { weekNumber: number }[] = []

    let current = start.clone();

    while (current.isSame(end) || current.isBefore(end)) {
      weeks.push({
        weekNumber: current.isoWeek(),
        // weekStart: current.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        // weekEnd: current.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      });

      current = current.add(1, 'week')
    }

    return 'week ' + (weeks.length > 0 ? weeks[0].weekNumber : ''); // Added safety check
  }

  private hasMoreThan3YearsOfData(): boolean {
    const allData = [
      ...(Array.isArray(this.data1) ? this.data1 : []),
      ...(Array.isArray(this.data2) ? this.data2 : []),
      ...(Array.isArray(this.data3) ? this.data3 : []),
      ...(Array.isArray(this.data4) ? this.data4 : [])
    ];
    if (allData.length === 0) return false;

    const uniqueYears = new Set<number>();
    allData.forEach(d => {
      if (!d) return;
      const yr = this.getYearFromData(d);
      if (yr !== null && !isNaN(yr)) {
        uniqueYears.add(yr);
      }
    });

    return uniqueYears.size > 3;
  }

  private has5OrMoreYearsOfData(): boolean {
    const allData = [
      ...(Array.isArray(this.data1) ? this.data1 : []),
      ...(Array.isArray(this.data2) ? this.data2 : []),
      ...(Array.isArray(this.data3) ? this.data3 : []),
      ...(Array.isArray(this.data4) ? this.data4 : [])
    ];
    if (allData.length === 0) return false;

    const uniqueYears = new Set<number>();
    allData.forEach(d => {
      if (!d) return;
      const yr = this.getYearFromData(d);
      if (yr !== null && !isNaN(yr)) {
        uniqueYears.add(yr);
      }
    });

    return uniqueYears.size >= 5;
  }

  private getYearFromData(d: any): number | null {
    if (!d) return null;

    // 1. Try start/end/date properties
    if (d.start) {
      const yr = dayjs(d.start).year();
      if (!isNaN(yr)) return yr;
    }
    if (d.end) {
      const yr = dayjs(d.end).year();
      if (!isNaN(yr)) return yr;
    }
    if (d.date) {
      const yr = dayjs(d.date).year();
      if (!isNaN(yr)) return yr;
    }

    // 2. Try label property
    if (d.label) {
      const labelStr = String(d.label).trim();
      // Check if it's a 4-digit number (e.g. "2024")
      if (/^\d{4}$/.test(labelStr)) {
        const yr = parseInt(labelStr, 10);
        if (!isNaN(yr)) return yr;
      }

      // Check for year match (e.g. "Jan 2024" or "2024-05")
      const match = labelStr.match(/\b(20\d{2}|19\d{2})\b/);
      if (match) {
        return parseInt(match[1], 10);
      }

      // Parse using dayjs
      const parsed = dayjs(labelStr);
      if (parsed.isValid()) {
        return parsed.year();
      }
    }

    return null;
  }
}